import { Checkbox } from '../../../ui/component/checkbox';
import { UserIcon } from '@heroicons/react/24/outline';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  UserCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { 
  TextInput, 
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  SelectItem,
} from '@tremor/react';
import { RiSearchLine } from '@remixicon/react';
import { FunnelIcon } from '@heroicons/react/24/solid';
import { Pagination } from '../../../ui/component/pagination';
import timeaway from '../../../network/timeaway';
import usePagination from '../../../hooks/usePagination';
import { useState, useRef, useEffect } from 'react';
import {
  getTimeawayDecisionLabel,
  getTimeawayTypeLabel,
} from '../../../utils/timeaway';
import BulkApproveModal from './BulkApproveModal';
import BulkRejectModal from './BulkRejectModal';
import { useLeaveTypes } from '../../../hooks/useLeaveTypes';

export function UserTable({
  handleApproveModal,
  handleRejectModal,
  handleRequestChangesModal,
  handleEmployeeDetails,
  onDataChanged,
}: any) {
  const limit = 10;
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  // Includes deactivated types: their requests still need filtering.
  const { leaveTypes } = useLeaveTypes({ includeInactive: true });
  const [filters, setFilters] = useState({
    leaveType: '',
    status: '',
  });
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: users = [],
    page,
    totalItems,
    setDirectPage,
  } = usePagination({
    key: 'users_requests',
    params: { 
      manager: true, 
      leaveType: filters.leaveType, 
      status: filters.status, 
      search: searchQuery 
    },
    fetcher: timeaway.getTimesOff,
    initialPage: 0,
    initialItemsPerPage: limit,
  });

  const handleSelectRow = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev: string[]) => [...prev, userId]);
    } else {
      setSelectedUsers((prev: string[]) => prev.filter((id) => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(users?.map((user: any) => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Move these declarations to the top with other state variables
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [isOpenBulkApproveModal, setIsOpenBulkApproveModal] = useState(false);
  const [isOpenBulkRejectModal, setIsOpenBulkRejectModal] = useState(false);

  // Add temporary filter state
  const [tempFilters, setTempFilters] = useState({
    leaveType: '',
    status: '',
  });

  // Then your useEffect that depends on showFilterDropdown
  useEffect(() => {
    if (showFilterDropdown) {
      setTempFilters({...filters});
    }
  }, [showFilterDropdown, filters]);

  // Apply filters only when apply button is clicked
  const applyFilter = () => {
    setFilters({...tempFilters});
    setShowFilterDropdown(false);
  };

  // Reset only resets temp filters until Apply is clicked
  const resetFilters = () => {
    setTempFilters({ leaveType: '', status: '' });
  };

  const isAllSelected = users.length > 0 && selectedUsers.length === users.length;
  const activeFilterCount = (filters.leaveType ? 1 : 0) + (filters.status ? 1 : 0);

  // Add dependency on filters to trigger refetch when they change
  useEffect(() => {
    // Optionally you could add a small delay before refetching
    if (filters.leaveType || filters.status) {
      // If you have a refetch function from usePagination, call it here
    }
  }, [filters]);

  return (
    <div className="mt-2 rounded-xl border-[1px] border-gray-100 px-6 py-2 shadow">
      <div className="flex flex-row justify-end gap-4 mb-4">
        {selectedUsers.length > 0 && (
          <div className="flex items-center gap-2 mr-auto">
            <p className="text-sm text-gray-600">
              {selectedUsers.length} selected
            </p>
            <Button
              variant="secondary"
              onClick={() => setIsOpenBulkApproveModal(true)}
            >
              Approve selected
            </Button>
            <Button
              variant="secondary"
              onClick={() => setIsOpenBulkRejectModal(true)}
            >
              Reject selected
            </Button>
            <Button variant="secondary" onClick={() => setSelectedUsers([])}>
              Clear
            </Button>
          </div>
        )}
        <TextInput
          icon={RiSearchLine}
          placeholder="Search"
          className="w-auto lg:w-[200px]"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        
        <div className="relative" ref={filterRef}>
          <Button 
            icon={FunnelIcon} 
            iconPosition="left" 
            variant="secondary" 
            size="md"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            aria-label="Filter options"
          >
            {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter"}
          </Button>
          
          {showFilterDropdown && (
            <div className="absolute right-0 top-full mt-1 z-10 w-64 bg-white rounded-md shadow-lg border border-gray-200">
              <div className="p-4">
                <h3 className="text-sm font-medium mb-3">Filter by</h3>
                
                <div className="mb-3">
                  <p className="text-xs font-medium mb-1">Leave Type</p>
                  <Select
                    id="leaveTypeSelect"
                    value={tempFilters.leaveType}
                    onValueChange={(value: string) => setTempFilters({...tempFilters, leaveType: value})}
                  >
                    <SelectItem value="">All</SelectItem>
                    {leaveTypes.map((type) => (
                      <SelectItem key={type.key} value={type.key}>
                        {type.active ? type.name : `${type.name} (inactive)`}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                
                <div className="mb-4">
                  <p className="text-xs font-medium mb-1">Status</p>
                  <Select
                    id="statusSelect"
                    value={tempFilters.status}
                    onValueChange={(value: string) => setTempFilters({...tempFilters, status: value})}
                  >
                    <SelectItem value="">All</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="declined">Rejected</SelectItem>
                  </Select>
                </div>
                
                <div className="flex justify-between">
                  <Button variant="secondary" onClick={resetFilters}>Reset</Button>
                  <Button 
                    variant="primary" 
                    onClick={applyFilter}
                  >
                    Apply
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>
              <div className="flex items-center gap-3">
                <Checkbox
                  className="rounded-md border-[1px]"
                  color="rose"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                />
                Employee
              </div>
            </TableHeaderCell>
            <TableHeaderCell>Leave type</TableHeaderCell>
            <TableHeaderCell>Dates Requested</TableHeaderCell>
            <TableHeaderCell>Duration</TableHeaderCell>
            <TableHeaderCell>Approval status</TableHeaderCell>
            <TableHeaderCell></TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users &&
            users?.map((user: any) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      className="rounded-md border-[1px]"
                      color="rose"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(checked) => handleSelectRow(user.id, checked)}
                    />
                    <UserIcon className="w-5" />
                    {user?.full_name}
                  </div>
                </TableCell>
                <TableCell>{getTimeawayTypeLabel(user?.type)}</TableCell>
                <TableCell>{user?.start} - {user?.end}</TableCell>
                <TableCell>{user?.details}</TableCell>
                <TableCell>
                  {user.decision === 'pending' ? (
                    <div className="flex max-w-[90px] justify-center rounded-lg bg-[#FFEDD5] px-2 py-1 text-[#C2410C]">
                      Pending
                    </div>
                  ) : user.decision === 'approved' ? (
                    <p className="flex max-w-[90px] justify-center rounded-lg bg-[#DCFCE7] px-2 py-1 text-[#15803D]">
                      Approved
                    </p>
                  ) : user.decision === 'declined' ? (
                    <div className="flex max-w-[90px] justify-center rounded-lg bg-[#FEE2E2] px-2 py-1 text-[#DC2626]">
                      Rejected
                    </div>
                  ) : (
                    <div className="flex max-w-[200px] justify-center text-wrap rounded-lg bg-[#FEF9C3] px-2 py-1 text-[#A16207]">
                      {getTimeawayDecisionLabel(user?.decision)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="relative" ref={actionMenuRef}>
                    <button 
                      onClick={() => setOpenActionMenu(openActionMenu === user.id ? null : user.id)}
                      aria-label="More options"
                    >
                      <EllipsisHorizontalIcon className="h-5 w-5 cursor-pointer" />
                    </button>
                    
                    {openActionMenu === user.id && (
                      <div className="absolute right-0 z-10 w-48 rounded-md bg-white shadow-lg">
                        <div className="p-2">
                          <button 
                            onClick={() => handleApproveModal(user)}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded"
                          >
                            <CheckCircleIcon color="gray" className="w-4" />
                            Approve
                          </button>
                          <button 
                            onClick={() => handleRejectModal(user)}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded"
                          >
                            <XCircleIcon color="gray" className="w-4" />
                            Reject
                          </button>
                          <button 
                            onClick={() => handleRequestChangesModal?.(user)}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded"
                          >
                            <ArrowPathIcon color="gray" className="w-4" />
                            Request changes
                          </button>
                          <button 
                            onClick={() => handleEmployeeDetails(user.id)}
                            className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded"
                          >
                            <UserCircleIcon color="gray" className="w-4" />
                            Employee Details
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
      
      <Pagination
        limit={limit}
        count={totalItems}
        page={page}
        onPageChange={setDirectPage}
      />

      <BulkApproveModal
        open={isOpenBulkApproveModal}
        setOpen={setIsOpenBulkApproveModal}
        ids={selectedUsers}
        onApproveSuccess={() => {
          setSelectedUsers([]);
          onDataChanged?.();
        }}
      />
      <BulkRejectModal
        open={isOpenBulkRejectModal}
        setOpen={setIsOpenBulkRejectModal}
        ids={selectedUsers}
        onRejectSuccess={() => {
          setSelectedUsers([]);
          onDataChanged?.();
        }}
      />
    </div>
  );
}